import {
  Card,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import type { AgBarSeriesOptions } from 'ag-charts-enterprise';
import dayjs from 'dayjs';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import VerticalBarChart from '@/components/charts/VerticalBarChart';
import GenericChartWidget from '@/components/GenericChartWidget/GenericChartWidget';
import LoadingIndicator from '@/components/LoadingIndicator';
import type { PercentageBarDataType } from '@/components/PercentageBarWidget';
import PercentageBarChart from '@/components/PercentageBarWidget/PercentageBarWidget';
import SegmentedProgressBar from '@/components/SegmentedProgressBar';
import type { SegmentedProgressBarProps } from '@/components/SegmentedProgressBar/SegmentedProgressBar.types';
import { YEAR_MONTH_DAY_FORMAT } from '@/constants/date.constants';
import PRODUCTS from '@/constants/products.constants';
import MCSCSplashPage from '@/features/MCSCSplashPage';
import useProductsCheck from '@/hooks/useProductsCheck';
import userAtom from '@/store/user.store';
import { useFetchMCSC } from '../Dashboard.hooks';
import { mcscAtom, selectedMCSCAssessmentIdAtom } from '../Dashboard.store';
import type { MyCyberScorecardDTO } from '../Dashboard.types';
import { DEFAULT_MCSC_ASSESSMENT } from '../dashboard.constants';
import {
  cyberRiskFunctionsAtom,
  cyberScoreCategoriesAtom,
  cyberScoreFunctionsAtom,
  cyberScoreFunctionsCompletionAtom,
  cyberScoreRiskItemsAtom
} from './DashboardMCSC.store';
import useStyles from './DashboardMCSC.styles';
import { MCSCSmallCards } from './MCSCSmallCards';

const DashboardMCSC = () => {
  const theme = useTheme();
  const [user] = useAtom(userAtom);
  const [cyberScoreFunctions] = useAtom(cyberScoreFunctionsAtom);
  const [cyberScoreRiskItems] = useAtom(cyberScoreRiskItemsAtom);
  const [scoreFunctionsCompletion] = useAtom(cyberScoreFunctionsCompletionAtom);
  const [scoreCategories] = useAtom(cyberScoreCategoriesAtom);
  const [selectedMCSCAssessmentId] = useAtom(selectedMCSCAssessmentIdAtom);
  const [mcscData] = useAtom(mcscAtom);
  const [riskFunctions] = useAtom(cyberRiskFunctionsAtom);

  const showSplashPage = user?.preferences?.show_mcsc_splash_page || true;

  const hasMCSCProducts = useProductsCheck([PRODUCTS.MCSC]);

  const { isFetching: isFetchingMCSCData } = useFetchMCSC();

  const { classes } = useStyles();

  const selectedMCSCAssessment = useMemo(() => {
    return (
      mcscData?.find(
        (item) => item.assessment_id === selectedMCSCAssessmentId
      ) || DEFAULT_MCSC_ASSESSMENT
    );
  }, [mcscData, selectedMCSCAssessmentId]);

  const stackedAssessmentWidget = useMemo(() => {
    return (
      <>
        <GenericChartWidget title="Risk Items" loading={isFetchingMCSCData}>
          <SegmentedProgressBar
            data={cyberScoreRiskItems as SegmentedProgressBarProps[]}
          />
        </GenericChartWidget>
        <Card className={classes.card}>
          <CardHeader
            className={classes.cardHeader}
            title="Assessment Information"
            slotProps={{ title: { variant: 'body1' } }}
          />
          <CardContent className={classes.cardContent}>
            {isFetchingMCSCData && (
              <div className={classes.widgetLoading}>
                <LoadingIndicator noBackground isLoading={isFetchingMCSCData} />
              </div>
            )}
            {!isFetchingMCSCData && (
              <Stack
                direction="column"
                sx={{ justifyContent: 'space-evenly', height: '100%' }}
              >
                <Typography className={classes.cardInformationText}>
                  <strong>Assessment Type:</strong>{' '}
                  {selectedMCSCAssessment?.assessment_type}
                </Typography>
                <Typography className={classes.cardInformationText}>
                  <strong>Status:</strong>{' '}
                  {(selectedMCSCAssessment?.status ?? '')
                    .charAt(0)
                    .charAt(0)
                    .toUpperCase() +
                    (selectedMCSCAssessment?.status ?? '').slice(1)}
                </Typography>
                <Typography className={classes.cardInformationText}>
                  <strong>Assessment Start Date:</strong>{' '}
                  {dayjs
                    .utc(selectedMCSCAssessment?.created_at)
                    .local()
                    .format(YEAR_MONTH_DAY_FORMAT)}
                </Typography>
                <Typography className={classes.cardInformationText}>
                  <strong>Assessment End Date:</strong>{' '}
                  {dayjs
                    .utc(selectedMCSCAssessment?.completed_at)
                    .local()
                    .format(YEAR_MONTH_DAY_FORMAT)}
                </Typography>
              </Stack>
            )}
          </CardContent>
        </Card>
      </>
    );
  }, [cyberScoreRiskItems, isFetchingMCSCData]);

  const scoreFunctionCompletion = useMemo(() => {
    return (
      <GenericChartWidget
        title="MyCyberScore Function Completion"
        loading={isFetchingMCSCData}
      >
        <PercentageBarChart
          data={scoreFunctionsCompletion as PercentageBarDataType[]}
        />
      </GenericChartWidget>
    );
  }, [cyberScoreRiskItems, isFetchingMCSCData]);

  const handleScoreColors = (value: number) => {
    if (value >= 0.9) {
      return theme.palette.success.main;
    }
    if (value > 0.5 && value < 0.9) {
      return theme.palette.warning.main;
    }
    if (value <= 0.5) {
      return theme.palette.error.main;
    }
    return theme.palette.info.main;
  };

  const functionCompletionSeries: AgBarSeriesOptions[] = useMemo(
    () => [
      {
        type: 'bar',
        xKey: 'name',
        yKey: 'target',
        yName: 'Target',
        fill: '#005000'
      },
      {
        type: 'bar',
        xKey: 'name',
        yKey: 'total',
        yName: 'Total',
        fill: handleScoreColors(
          (selectedMCSCAssessment?.my_cyber_score?.total ?? 0) /
            (selectedMCSCAssessment?.my_cyber_score?.target ?? 1)
        )
      }
    ],
    [selectedMCSCAssessment]
  );

  const scoreByFunction = useMemo(() => {
    return (
      <GenericChartWidget
        title="MyCyberScore By Function"
        loading={isFetchingMCSCData}
      >
        <VerticalBarChart
          data={cyberScoreFunctions}
          series={functionCompletionSeries}
        />
      </GenericChartWidget>
    );
  }, [isFetchingMCSCData, cyberScoreFunctions, functionCompletionSeries]);

  const categoriesCompletionSeries: AgBarSeriesOptions[] = useMemo(
    () => [
      {
        type: 'bar',
        xKey: 'name',
        yKey: 'target',
        yName: 'Target',
        fill: '#005000'
      },
      {
        type: 'bar',
        xKey: 'name',
        yKey: 'score',
        yName: 'Score',
        fill: handleScoreColors(
          (selectedMCSCAssessment?.my_cyber_score?.total ?? 1) /
            (selectedMCSCAssessment?.my_cyber_score?.target ?? 0)
        )
      }
    ],
    [selectedMCSCAssessment]
  );

  const scoreByCategory = useMemo(() => {
    return (
      <GenericChartWidget
        title="MyCyberScore By Category"
        loading={isFetchingMCSCData}
      >
        <VerticalBarChart
          data={scoreCategories}
          series={categoriesCompletionSeries}
        />
      </GenericChartWidget>
    );
  }, [isFetchingMCSCData, scoreCategories]);

  const riskFunctionCompletionSeries: AgBarSeriesOptions[] = useMemo(
    () => [
      {
        type: 'bar',
        xKey: 'name',
        yKey: 'total',
        yName: 'Total',
        fill: handleScoreColors(
          (selectedMCSCAssessment?.my_cyber_risk?.total ?? 0) /
            (selectedMCSCAssessment?.my_cyber_risk?.target ?? 1)
        )
      }
    ],
    [selectedMCSCAssessment]
  );

  const riskByFunction = useMemo(() => {
    return (
      <GenericChartWidget
        title="MyCyberRisk By Function"
        loading={isFetchingMCSCData}
      >
        <VerticalBarChart
          data={riskFunctions}
          series={riskFunctionCompletionSeries}
        />
      </GenericChartWidget>
    );
  }, [isFetchingMCSCData, riskFunctions, riskFunctionCompletionSeries]);

  return (
    <>
      {!hasMCSCProducts && showSplashPage && <MCSCSplashPage />}
      <div
        style={{
          filter: !hasMCSCProducts && showSplashPage ? 'blur(5px)' : 'none',
          height: '100%'
        }}
      >
        <div className={classes.dashboardPage}>
          <MCSCSmallCards
            selectedMCSCAssessment={
              selectedMCSCAssessment as MyCyberScorecardDTO
            }
          />
          <div className={classes.dashboardLargeItem}>{scoreByFunction}</div>
          <div className={classes.dashboardLargeItem}>{riskByFunction}</div>
          <div className={classes.dashboardMediumItem}>{scoreByCategory}</div>
          <div className={classes.dashboardMediumItem}>
            {scoreFunctionCompletion}
          </div>
          <div className={classes.dashboardStackedItem}>
            {stackedAssessmentWidget}
          </div>
        </div>
      </div>
    </>
  );
};

export default DashboardMCSC;
